import { Dispatch } from 'redux';
import { ActionType } from '../action-types';
import { Action } from '../actions';
import {
  APIResponseStatusType,
  GooogleAnalyticsActionType,
  GooogleAnalyticsCategoryType,
} from '../../models/types';
import {
  InstallationRequestGET,
  InstallationResponseGET,
  LoginRequestPOST,
  LoginResponsePOST,
  MeRequestGET,
  MeResponseGET,
  SessionRequestPOST,
  SessionResponsePOST,
} from '../../models/cruds';
import { TrackGoogleAnalyticsEvent } from '../../utils/google-analytics/TrackGoogleAnalyticsEvent';
import { apiErrorHandler, apiGET, apiPOST } from '../../restApi';
import { indexedDBInstance } from '../../indexedDB';

export const sessionUpdateDHLToken = (token: string) => {
  return (dispatch: Dispatch<Action>) => {
    dispatch({ type: ActionType.UPDATE_DHL_API_TOKEN, payload: token });
  };
};
export const sessionUpdateAgentToken = (token: string) => {
  return (dispatch: Dispatch<Action>) => {
    dispatch({ type: ActionType.UPDATE_DHL_AGENT_TOKEN, payload: token });
  };
};
export const sessionUpdateDHLTokenExpirationTime = (
  expiration_time: number
) => {
  return (dispatch: Dispatch<Action>) => {
    dispatch({
      type: ActionType.UPDATE_DHL_API_TOKEN_EXPIRATION_TIME,
      payload: expiration_time,
    });
  };
};
export const sessionUpdateDHLTokenExpired = (expired: boolean) => {
  return (dispatch: Dispatch<Action>) => {
    dispatch({
      type: ActionType.UPDATE_DHL_API_TOKEN_EXPIRED,
      payload: expired,
    });
  };
};
export const sessionClearDHLToken = () => {
  return (dispatch: Dispatch<Action>) => {
    dispatch({ type: ActionType.RESET_DHL_API_TOKEN });
  };
};
export const sessionExitShowModal = (show: boolean) => {
  return (dispatch: Dispatch<Action>) => {
    dispatch({ type: ActionType.SESSION_EXIT_SHOW_MODAL, payload: show });
  };
};
export const userInactivityShowModal = (show: boolean) => {
  return (dispatch: Dispatch<Action>) => {
    dispatch({ type: ActionType.USER_INACTIVITY_SHOW_MODAL, payload: show });
  };
};
export const updateUserInactivitySeconds = (seconds: number) => {
  return (dispatch: Dispatch<Action>) => {
    dispatch({
      type: ActionType.UPDATE_USER_INACTIVITY_SECONDS,
      payload: seconds,
    });
  };
};
export const sessionNeedsLogin = () => {
  return (dispatch: Dispatch<Action>) => {
    dispatch({
      type: ActionType.UPDATE_NEEDS_LOGIN,
    });
  };
};
export const sessionNeedsSessionOpen = (needsSessionOpen: boolean) => {
  return (dispatch: Dispatch<Action>) => {
    dispatch({
      type: ActionType.UPDATE_NEEDS_SESSION_OPEN,
      payload: needsSessionOpen,
    });
  };
};
export const sessionReset = () => {
  return (dispatch: Dispatch<Action>) => {
    dispatch({
      type: ActionType.SESSION_RESET,
    });
  };
};

export const sessionAuthentication = (
  username: string,
  password: string,
  installationId?: string,
  token?: string
) => {
  return async (dispatch: Dispatch<Action>) => {
    const apiLogin = async () => {
      dispatch({
        type: ActionType.UPDATE_AUTH_ERROR_MESSAGE,
        payload: '',
      });
      dispatch({
        type: ActionType.UPDATE_AUTHENTICATING,
        payload: true,
      });
      let action = GooogleAnalyticsActionType.LOGIN;
      let category = GooogleAnalyticsCategoryType.REQUEST;
      TrackGoogleAnalyticsEvent(action, category, '');
      let request = new LoginRequestPOST();
      let response: LoginResponsePOST = await apiPOST(
        request.buildURL(),
        request.generateContent(username, password),
        request.generateConfig()
      );
      let errorMsg = '';
      try {
        if (response.status === APIResponseStatusType.SUCCESS) {
          let token = response.data.access_token;
          if (token) {
            let expiration_time =
              Date.now() + (response.data.expires_in - 2000) * 1000;
            dispatch({
              type: ActionType.UPDATE_DHL_API_TOKEN_EXPIRATION_TIME,
              payload: expiration_time,
            });
            dispatch({
              type: ActionType.UPDATE_DHL_API_TOKEN_EXPIRED,
              payload: false,
            });
            dispatch({ type: ActionType.UPDATE_DHL_API_TOKEN, payload: token });
            apiMe(response.data.access_token);
            return;
          } else {
            errorMsg = 'Unauthorized';
          }
        } else {
          errorMsg = apiErrorHandler(response);
        }
      } catch (err: any) {
        errorMsg = err.toString();
      }
      console.log('Login failed: ', errorMsg);

      action = GooogleAnalyticsActionType.LOGIN;
      category = GooogleAnalyticsCategoryType.FAIL;
      TrackGoogleAnalyticsEvent(action, category, errorMsg);
      dispatch({
        type: ActionType.UPDATE_AUTH_ERROR_MESSAGE,
        payload: errorMsg,
      });
      dispatch({
        type: ActionType.UPDATE_NEEDS_LOGIN,
      });
    };
    const apiMe = async (accessToken: string) => {
      let request = new MeRequestGET();
      let response: MeResponseGET = await apiGET(
        request.buildURL(),
        request.generateConfig(accessToken)
      );
      let errorMsg = '';
      try {
        if (response.status === APIResponseStatusType.SUCCESS) {
          let user = response.data.user;
          if (user) {
            let action = GooogleAnalyticsActionType.LOGIN;
            let category = GooogleAnalyticsCategoryType.SUCCESS;
            TrackGoogleAnalyticsEvent(
              action,
              category,
              response.data.user.username
            );

            if (user.session) {
              getKioskInstallation(
                accessToken,
                user.session.installation_id.toString()
              );
            } else {
              dispatch({
                type: ActionType.UPDATE_NEEDS_SESSION_OPEN,
                payload: true,
              });
            }
            return;
          } else {
            errorMsg = 'No user in login info';
          }
        } else {
          errorMsg = apiErrorHandler(response);
        }
      } catch (err: any) {
        errorMsg = err.toString();
      }
      let action = GooogleAnalyticsActionType.LOGIN;
      let category = GooogleAnalyticsCategoryType.FAIL;
      TrackGoogleAnalyticsEvent(action, category, errorMsg);
      dispatch({
        type: ActionType.UPDATE_AUTH_ERROR_MESSAGE,
        payload: errorMsg,
      });
      dispatch({
        type: ActionType.UPDATE_NEEDS_LOGIN,
      });
    };
    const getKioskInstallation = async (
      accessToken: string,
      installationId: string
    ) => {
      let request = new InstallationRequestGET();
      let response: InstallationResponseGET = await apiGET(
        request.buildURL(installationId),
        request.generateConfig(accessToken)
      );
      let errorMsg = '';
      try {
        if (response.status === APIResponseStatusType.SUCCESS) {
          if (response.data.installation) {
            let installation = response.data.installation;
            dispatch({
              type: ActionType.UPDATE_KIOSK_INSTALLATION,
              payload: installation,
            });
            dispatch({
              type: ActionType.UPDATE_AUTHENTICATING,
              payload: false,
            });
            await indexedDBInstance.setItem('username', username);
            await indexedDBInstance.setItem('password', password);
            await indexedDBInstance.setItem('installationId', installationId);
            return;
          } else {
            errorMsg = 'No installation found';
          }
        } else {
          errorMsg = apiErrorHandler(response);
        }
      } catch (err: any) {
        errorMsg = err.toString();
      }
      console.error(errorMsg);
      dispatch({
        type: ActionType.UPDATE_AUTH_ERROR_MESSAGE,
        payload: errorMsg,
      });
      dispatch({
        type: ActionType.UPDATE_NEEDS_LOGIN,
      });
    };
    const openSession = async (installationId: string, token: string) => {
      dispatch({
        type: ActionType.UPDATE_AUTH_ERROR_MESSAGE,
        payload: '',
      });
      let request = new SessionRequestPOST();
      let response: SessionResponsePOST = await apiPOST(
        request.buildURL(),
        request.generateContent(installationId),
        request.generateConfig(token)
      );
      let errorMsg = '';
      try {
        if (response.status === APIResponseStatusType.SUCCESS) {
          if (response.data.session) {
            let sess = response.data.session;
            getKioskInstallation(token, sess.installation_id.toString());
            return;
          } else {
            errorMsg = 'No session Opened';
          }
        } else {
          errorMsg = apiErrorHandler(response);
        }
      } catch (err: any) {
        errorMsg = err.toString();
      }
      console.log(errorMsg);
      dispatch({
        type: ActionType.UPDATE_AUTH_ERROR_MESSAGE,
        payload: errorMsg,
      });
      dispatch({
        type: ActionType.UPDATE_NEEDS_LOGIN,
      });
    };
    if (installationId && token) {
      openSession(installationId, token);
    } else {
      apiLogin();
    }
  };
};
